import type { HsMatrix4 } from '../objects/matrix4.class';
import { HsVector3 } from '../objects/vector3.class';

export class HsBox {
  public readonly min: HsVector3;
  public readonly max: HsVector3;

  static readonly #vector3Temp1: HsVector3 = HsVector3.makeZeroVector();
  static readonly #vector3Temp2: HsVector3 = HsVector3.makeZeroVector();
  static readonly #vector3Temp3: HsVector3 = HsVector3.makeZeroVector();
  static readonly #vector3Temp4: HsVector3 = HsVector3.makeZeroVector();
  static readonly #vector3TempArray: HsVector3[] = [
    HsVector3.makeZeroVector(),
    HsVector3.makeZeroVector(),
    HsVector3.makeZeroVector(),
    HsVector3.makeZeroVector(),
    HsVector3.makeZeroVector(),
    HsVector3.makeZeroVector(),
    HsVector3.makeZeroVector(),
    HsVector3.makeZeroVector(),
  ];

  constructor(minX: number, minY: number, minZ: number, maxX: number, maxY: number, maxZ: number) {
    this.min = new HsVector3(minX, minY, minZ);
    this.max = new HsVector3(maxX, maxY, maxZ);
  }

  /**
   * Sets set.
   * @param min min.
   * @param max max.
   * @returns Result value.
   */
  public set(min: HsVector3, max: HsVector3): HsBox {
    this.min.copy(min);
    this.max.copy(max);
    return this;
  }

  /**
   * Copies copy.
   * @returns Result value.
   */
  public copy(other: HsBox): HsBox {
    this.min.copy(other.min);
    this.max.copy(other.max);
    return this;
  }

  /**
   * Gets center.
   * @param center center.
   * @returns Result value.
   */
  public getCenter(center: HsVector3): HsVector3 {
    return HsVector3.average(this.min, this.max, center);
  }

  /**
   * Gets size.
   * @param size size.
   * @returns Result value.
   */
  public getSize(size: HsVector3): HsVector3 {
    return size.subtractVectors(this.max, this.min);
  }

  /**
   * Handles is empty.
   * @returns Result value.
   */
  public isEmpty(): boolean {
    return this.max.x < this.min.x || this.max.y < this.min.y || this.max.z < this.min.z;
  }

  /**
   * Sets empty.
   * @returns Result value.
   */
  public setEmpty(): HsBox {
    this.min.set(+Infinity, +Infinity, +Infinity);
    this.max.set(-Infinity, -Infinity, -Infinity);
    return this;
  }

  /**
   * Handles expand by point.
   * @param point point.
   * @returns Result value.
   */
  public expandByPoint(point: HsVector3): HsBox {
    this.min.min(point);
    this.max.max(point);
    return this;
  }

  /**
   * Sets from points.
   * @param point point.
   * @returns Result value.
   */
  public setFromPoints(points: HsVector3[]): HsBox {
    this.setEmpty();
    for (let i: number = 0; i < points.length; i++) {
      this.expandByPoint(points[i]);
    }
    return this;
  }

  /**
   * Gets volume.
   * @returns Result value.
   */
  public getVolume(): number {
    this.getSize(HsBox.#vector3Temp1);
    if (HsBox.#vector3Temp1.x < 0 || HsBox.#vector3Temp1.y < 0 || HsBox.#vector3Temp1.z < 0) return 0;
    return HsBox.#vector3Temp1.x * HsBox.#vector3Temp1.y * HsBox.#vector3Temp1.z;
  }

  /**
   * Handles apply matrix4.
   * @param matrix matrix.
   * @returns Result value.
   */
  public applyMatrix4(matrix: HsMatrix4): HsBox {
    if (this.isEmpty()) {
      return this;
    }
    HsBox.#vector3TempArray[0].set(this.min.x, this.min.y, this.min.z).applyMatrix4(matrix.values);
    HsBox.#vector3TempArray[1].set(this.min.x, this.min.y, this.max.z).applyMatrix4(matrix.values);
    HsBox.#vector3TempArray[2].set(this.min.x, this.max.y, this.min.z).applyMatrix4(matrix.values);
    HsBox.#vector3TempArray[3].set(this.min.x, this.max.y, this.max.z).applyMatrix4(matrix.values);
    HsBox.#vector3TempArray[4].set(this.max.x, this.min.y, this.min.z).applyMatrix4(matrix.values);
    HsBox.#vector3TempArray[5].set(this.max.x, this.min.y, this.max.z).applyMatrix4(matrix.values);
    HsBox.#vector3TempArray[6].set(this.max.x, this.max.y, this.min.z).applyMatrix4(matrix.values);
    HsBox.#vector3TempArray[7].set(this.max.x, this.max.y, this.max.z).applyMatrix4(matrix.values);
    return this.setFromPoints(HsBox.#vector3TempArray);
  }

  /**
   * Sets from array.
   * @param pointsArray points array.
   * @returns Result value.
   */
  public setFromArray(pointsArray: Float32Array | number[]): HsBox {
    let minX: number = +Infinity;
    let minY: number = +Infinity;
    let minZ: number = +Infinity;
    let maxX: number = -Infinity;
    let maxY: number = -Infinity;
    let maxZ: number = -Infinity;
    for (let i: number = 0; i < pointsArray.length; i += 3) {
      const x: number = pointsArray[i];
      const y: number = pointsArray[i + 1];
      const z: number = pointsArray[i + 2];
      minX = Math.min(x, minX);
      minY = Math.min(y, minY);
      minZ = Math.min(z, minZ);
      maxX = Math.max(x, maxX);
      maxY = Math.max(y, maxY);
      maxZ = Math.max(z, maxZ);
    }
    this.min.set(minX, minY, minZ);
    this.max.set(maxX, maxY, maxZ);
    return this;
  }

  /**
   * Handles union.
   * @param box box.
   * @returns Result value.
   */
  public union(box: HsBox): HsBox {
    this.min.min(box.min);
    this.max.max(box.max);
    return this;
  }

  /**
   * Handles scale.
   * @param value value.
   * @returns Result value.
   */
  public scale(value: number): HsBox {
    this.getCenter(HsBox.#vector3Temp2);
    this.min.subtract(HsBox.#vector3Temp2);
    this.max.subtract(HsBox.#vector3Temp2);
    this.min.multiplyByScalar(value);
    this.max.multiplyByScalar(value);
    this.min.add(HsBox.#vector3Temp2);
    this.max.add(HsBox.#vector3Temp2);
    return this;
  }

  /**
   * Builds empty.
   * @returns Result value.
   */
  public static makeEmpty(): HsBox {
    return new HsBox(+Infinity, +Infinity, +Infinity, -Infinity, -Infinity, -Infinity);
  }

  /**
   * Builds unit cube.
   * @returns Result value.
   */
  public static makeUnitCube(): HsBox {
    return new HsBox(-0.5, -0.5, -0.5, 0.5, 0.5, 0.5);
  }

  /**
   * Builds from array.
   * @param pointsArray points array.
   * @returns Result value.
   */
  public static makeFromArray(pointsArray: Float32Array | number[]): HsBox {
    const box: HsBox = HsBox.makeEmpty();
    return box.setFromArray(pointsArray);
  }

  /**
   * Handles distance.
   * @param point point.
   * @returns Result value.
   */
  public static distance(box: HsBox, point: HsVector3): number {
    HsBox.#vector3Temp3.copy(point).clamp(box.min, box.max);
    return HsVector3.distance(point, HsBox.#vector3Temp3);
  }

  /**
   * Handles intersects sphere.
   * @param sphere sphere.
   * @returns Result value.
   */
  public static intersectsSphere(box: HsBox, center: HsVector3, radius: number): boolean {
    HsBox.#vector3Temp4.copy(center).clamp(box.min, box.max);
    return HsVector3.distanceSquared(HsBox.#vector3Temp4, center) <= radius * radius;
  }

  /**
   * Builds copy.
   * @param source source.
   * @returns Result value.
   */
  public static makeCopy(source: HsBox): HsBox {
    return new HsBox(
      source.min.values[0],
      source.min.values[1],
      source.min.values[2],
      source.max.values[0],
      source.max.values[1],
      source.max.values[2]
    );
  }
}
